import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { signalStore, withMethods } from '@ngrx/signals';
import { withCalls, withLogger } from '@ngrx-traits/signals';
import { callConfig } from '@ngrx-traits/signals';
import { ViewConfiguratorService } from '../data/view-configurator.service';
import {
  ExportableUiLayoutView,
  UiLayoutViewDto,
} from '../models/view-configurator-dto.model';
import { ViewConfigStore } from './view-config.store';

function downloadJson(data: object, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function parseJsonFile(file: File): Promise<ExportableUiLayoutView> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(
          reader.result as string
        ) as ExportableUiLayoutView;
        if (!data.title || !Array.isArray(data.uiLayoutGroups)) {
          reject(new Error('Invalid file format'));
          return;
        }
        resolve(data);
      } catch {
        reject(new Error('Invalid JSON file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export const ViewImportExportStore = signalStore(
  withCalls(() => {
    const service = inject(ViewConfiguratorService);
    const snackBar = inject(MatSnackBar);
    const configStore = inject(ViewConfigStore);

    return {
      exportView: callConfig({
        call: ({ view, viewId }: { view: UiLayoutViewDto; viewId: number }) =>
          service.exportView(view, viewId),
        onSuccess: (exportData, { view }) => {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          downloadJson(exportData, `${view.title}-${timestamp}.json`);
          snackBar.open('Ansicht exportiert', 'OK', { duration: 3000 });
        },
        onError: () => {
          snackBar.open('Export fehlgeschlagen', 'OK', { duration: 3000 });
        },
      }),

      importView: callConfig({
        call: ({
          data,
          dataTypeId,
        }: {
          data: ExportableUiLayoutView;
          dataTypeId: number;
        }) => service.importView(data, dataTypeId),
        onSuccess: (viewId) => {
          configStore.loadView({ viewId });
          snackBar.open('Ansicht importiert', 'OK', { duration: 3000 });
        },
        onError: () => {
          snackBar.open('Import fehlgeschlagen', 'OK', { duration: 3000 });
        },
      }),
    };
  }),

  withMethods((store) => {
    const snackBar = inject(MatSnackBar);

    return {
      async importFromFile(file: File, dataTypeId: number): Promise<void> {
        try {
          const data = await parseJsonFile(file);
          store.importView({ data, dataTypeId });
        } catch (error) {
          snackBar.open(
            error instanceof Error ? error.message : 'Import fehlgeschlagen',
            'OK',
            { duration: 3000 }
          );
        }
      },
    };
  }),

  withLogger({
    name: 'ViewImportExportStore',
    showDiff: true,
  })
);
